import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ThemeColors {
  primary: string;
  secondary: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  background: string;
  paper: string;
  text: {
    primary: string;
    secondary: string;
    disabled: string;
  };
  divider: string;
  chord: {
    text: string;
    background: string;
  };
}

interface ThemePreset {
  name: string;
  colors: ThemeColors;
}

interface ThemeStore {
  mode: 'light' | 'dark';
  colors: {
    light: ThemeColors;
    dark: ThemeColors;
  };
  presets: {
    light: ThemePreset[];
    dark: ThemePreset[];
  };
  toggleMode: () => void;
  updateColor: (key: string, value: string | any) => void;
  resetColors: () => void;
  applyPreset: (presetName: string) => void;
}

const themePresets = {
  light: [
    {
      name: 'Default',
      colors: {
        primary: '#1976d2',
        secondary: '#dc004e',
        success: '#2e7d32',
        warning: '#ed6c02',
        error: '#d32f2f',
        info: '#0288d1',
        background: '#f5f5f5',
        paper: '#ffffff',
        text: {
          primary: 'rgba(0, 0, 0, 0.87)',
          secondary: 'rgba(0, 0, 0, 0.6)',
          disabled: 'rgba(0, 0, 0, 0.38)'
        },
        divider: 'rgba(0, 0, 0, 0.12)',
        chord: {
          text: '#1976d2',
          background: 'transparent'
        }
      }
    },
    {
      name: 'High Contrast',
      colors: {
        primary: '#0052cc',
        secondary: '#de350b',
        success: '#00875a',
        warning: '#ff8b00',
        error: '#de350b',
        info: '#505f79',
        background: '#ffffff',
        paper: '#f4f5f7',
        text: {
          primary: '#172b4d',
          secondary: '#5e6c84',
          disabled: '#a5adba'
        },
        divider: '#dfe1e6',
        chord: {
          text: '#0052cc',
          background: 'rgba(0, 82, 204, 0.08)'
        }
      }
    },
    {
      name: 'Warm',
      colors: {
        primary: '#c2185b',
        secondary: '#7b1fa2',
        success: '#388e3c',
        warning: '#f57c00',
        error: '#d32f2f',
        info: '#1976d2',
        background: '#fafafa',
        paper: '#ffffff',
        text: {
          primary: 'rgba(0, 0, 0, 0.87)',
          secondary: 'rgba(0, 0, 0, 0.6)',
          disabled: 'rgba(0, 0, 0, 0.38)'
        },
        divider: 'rgba(0, 0, 0, 0.12)',
        chord: {
          text: '#c2185b',
          background: 'transparent'
        }
      }
    }
  ],
  dark: [
    {
      name: 'Default',
      colors: {
        primary: '#90caf9',
        secondary: '#f48fb1',
        success: '#66bb6a',
        warning: '#ffa726',
        error: '#f44336',
        info: '#29b6f6',
        background: '#121212',
        paper: '#1e1e1e',
        text: {
          primary: 'rgba(255, 255, 255, 0.87)',
          secondary: 'rgba(255, 255, 255, 0.6)',
          disabled: 'rgba(255, 255, 255, 0.38)'
        },
        divider: 'rgba(255, 255, 255, 0.12)',
        chord: {
          text: '#90caf9',
          background: 'transparent'
        }
      }
    },
    {
      name: 'High Contrast',
      colors: {
        primary: '#40a9ff',
        secondary: '#ff4d4f',
        success: '#52c41a',
        warning: '#faad14',
        error: '#ff4d4f',
        info: '#1890ff',
        background: '#000000',
        paper: '#141414',
        text: {
          primary: '#ffffff',
          secondary: '#d9d9d9',
          disabled: '#8c8c8c'
        },
        divider: 'rgba(255, 255, 255, 0.2)',
        chord: {
          text: '#40a9ff',
          background: 'rgba(64, 169, 255, 0.12)'
        }
      }
    },
    {
      name: 'Gruvbox Dark',
      colors: {
        primary: '#458588',
        secondary: '#fb4934',
        success: '#689d6a',
        warning: '#d79921',
        error: '#cc241d',
        info: '#928374',
        background: '#504945',
        paper: '#1e1e1e',
        text: {
          primary: '#ebdbb2',
          secondary: '#d5c4a1',
          disabled: '#928374'
        },
        divider: 'rgba(235, 219, 178, 0.12)',
        chord: {
          text: '#fabd2f',
          background: 'transparent'
        }
      }
    },
    {
      name: 'Cyberpunk',
      colors: {
        primary: '#00ff9f',
        secondary: '#ff006e',
        success: '#00ff9f',
        warning: '#ffb700',
        error: '#ff006e',
        info: '#00b4d8',
        background: '#0a0e27',
        paper: '#151935',
        text: {
          primary: '#ffffff',
          secondary: '#c9d1d9',
          disabled: '#6e7681'
        },
        divider: 'rgba(255, 255, 255, 0.1)',
        chord: {
          text: '#00ff9f',
          background: 'rgba(0, 255, 159, 0.08)'
        }
      }
    },
    {
      name: 'Nord',
      colors: {
        primary: '#88c0d0',
        secondary: '#bf616a',
        success: '#a3be8c',
        warning: '#ebcb8b',
        error: '#bf616a',
        info: '#5e81ac',
        background: '#2e3440',
        paper: '#3b4252',
        text: {
          primary: '#eceff4',
          secondary: '#e5e9f0',
          disabled: '#4c566a'
        },
        divider: 'rgba(236, 239, 244, 0.12)',
        chord: {
          text: '#88c0d0',
          background: 'transparent'
        }
      }
    }
  ]
};

const defaultColors = {
  light: themePresets.light[0].colors,  // Default light theme
  dark: themePresets.dark[0].colors     // Default dark theme
};

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      mode: 'dark',  // Start with dark mode to show Gruvbox theme
      colors: defaultColors,
      presets: themePresets,
      
      toggleMode: () => {
        set((state) => ({
          mode: state.mode === 'light' ? 'dark' : 'light'
        }));
      },
      
      updateColor: (key, value) => {
        const { mode } = get();
        set((state) => ({
          colors: {
            ...state.colors,
            [mode]: {
              ...state.colors[mode],
              [key]: value
            }
          }
        }));
      },
      
      resetColors: () => {
        set({ colors: defaultColors });
      },
      
      applyPreset: (presetName) => {
        const { mode, presets } = get();
        const preset = presets[mode].find(p => p.name === presetName);
        if (preset) {
          set((state) => ({
            colors: {
              ...state.colors,
              [mode]: preset.colors
            }
          }));
        }
      }
    }),
    {
      name: 'commonchord-theme',
      version: 1,
      // Add migration to fix corrupted state
      migrate: (persistedState: any, version: number) => {
        // If colors is missing or corrupted, reset to defaults
        if (!persistedState || 
            !persistedState.colors || 
            !persistedState.colors.light || 
            !persistedState.colors.dark ||
            !persistedState.colors.light.text ||
            !persistedState.colors.dark.text) {
          return {
            mode: persistedState?.mode || 'dark',
            colors: defaultColors,
            presets: themePresets
          };
        }
        return persistedState;
      }
    }
  )
);